import { toast } from '@alternative/acqua-ui'
import {
  createHttpLink,
  ObservableQuery,
  FetchResult,
  ApolloLink
} from '@apollo/client'
import { ErrorResponse } from '@apollo/client/link/error'
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import { Observable } from '@apollo/client/utilities'
import { GraphQLError } from 'graphql'
import Cookies from 'js-cookie'
import { PostHog } from 'posthog-node'
import { AppCookie, cookieOptions } from 'utils/cookies'
import { errorTracking, ssrErrorTracking } from 'utils/posthog'
import { v4 as uuidv4 } from 'uuid'

import { requestIdHeader } from './headers'
import {
  getOperationErrorProperties,
  getTransportErrorProperties
} from './utils'

export { requestIdHeader }
export { timingLink } from './timingLink'
export { createPageSizeLimitLink } from './pageSizeLimitLink'
export type { PageSizeLimitConfig } from './pageSizeLimitLink'

interface DelayRefetch {
  observableQuery: ObservableQuery
  ms?: number
}

const wait = (ms: number) => new Promise(res => setTimeout(res, ms))

export const delayRefetchedQuery = async ({
  observableQuery,
  ms = 1000
}: DelayRefetch) => {
  await wait(ms)
  observableQuery.refetch()
}

/**
 *
 * Handles GraphQL and network errors for Apollo Client, with support for authentication refresh and custom error handling.
 *
 * @param {ErrorResponse} errorResponse - The error response object from Apollo Client.
 * @param {Function} refreshFunction - A function that returns a Promise to refresh the authentication token.
 * @param {Function} customError - An optional function for custom error handling.
 * @param {GraphQLError} customError.error - The GraphQL error object to be handled.
 * @param {Function} customError.stop - A function to stop the default error handling after the custom error handling is done.
 *
 * @description
 * This function handles various types of errors that can occur during GraphQL operations. It also integrates with toast notifications for user feedback and Datadog RUM for error logging.
 *
 * @example
 * const paymentsErrorLink = onError(errors =>
 * handleErrors(errors, oauth2RefreshPayments, (error, stop) => {
 *   const { extensions, path } = error
 *   if (
 *     (extensions?.status as number) === 404 &&
 *     (path || [])[0] === 'GetUserInfo'
 *   ) {
 *     // if user is not found we don't want to show an error
 *     stop()
 *   }
 *  })
 * )
 *
 */
export const handleErrors = (
  { graphQLErrors, networkError, operation, forward }: ErrorResponse,
  refreshFunction: () => Promise<void>,
  customError?: (error: GraphQLError, stop: () => void) => void
) => {
  if (graphQLErrors && graphQLErrors?.length > 0) {
    for (const [index, error] of graphQLErrors.entries()) {
      const { extensions } = error
      // only process first error to avoid multiple toasts
      if (index === 0 && !networkError) {
        if (
          (extensions?.status as number) === 401 ||
          (extensions?.status as number) === 403
        ) {
          const observable = new Observable<
            FetchResult<Record<string, unknown>>
          >(observer => {
            const retryPreviousRequest = () => {
              const subscriber = {
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer)
              }

              forward(operation).subscribe(subscriber)
            }

            const handleUnauthorizedRequest = async () => {
              try {
                // the first request locks, refresh then retries request
                // consecutive requests wait for refresh then retry request
                await refreshLock(refreshFunction)
                retryPreviousRequest()
              } catch (err) {
                observer.error(err)
              }
            }

            handleUnauthorizedRequest()
          })

          return observable
        }

        // custom error handling
        let stopHandlingError = false
        if (customError) {
          customError(error, () => (stopHandlingError = true))
        }
        if (stopHandlingError) {
          return
        }

        if (
          extensions?.status === 500 &&
          operation.getContext().fallbackCachedData
        ) {
          errorTracking({
            error,
            properties: {
              error,
              status: extensions?.status,
              ...getOperationErrorProperties(operation)
            },
            fingerprint: 'apiError'
          })
          return
        }
        const suppressToast = operation.getContext().suppressToast

        if ((extensions?.status as number) < 500) {
          if (!suppressToast) {
            toast(
              {
                icon: 'error',
                title: 'Error',
                subtitle: error.message || 'An unknown error occurred',
                variant: 'error'
              },
              { id: 'error' }
            )
          }
          errorTracking({
            error,
            properties: {
              error,
              status: extensions?.status,
              ...getOperationErrorProperties(operation)
            },
            fingerprint: 'apiError'
          })
          return
        }

        errorTracking({
          error,
          properties: {
            error,
            status: extensions?.status,
            ...getOperationErrorProperties(operation)
          },
          fingerprint: 'serverError'
        })
        if (!suppressToast) {
          toast(
            {
              icon: 'error',
              title: 'Error',
              subtitle: error.message || 'An unknown error occurred',
              variant: 'error'
            },
            { id: 'error' }
          )
        }
      }
    }
  }

  if (networkError) {
    const suppressNetworkErrorTracking =
      operation.getContext().suppressNetworkErrorTracking === true

    // Polling surfaces (and other ambient network loss) can opt out of
    // $exception capture while still letting the caller render sticky UI.
    if (!suppressNetworkErrorTracking) {
      errorTracking({
        error: networkError,
        properties: {
          ...getTransportErrorProperties(networkError),
          ...getOperationErrorProperties(operation)
        },
        fingerprint: 'networkError'
      })
    }
  }
}

export const handleSSRErrors = (
  { graphQLErrors, networkError, operation }: ErrorResponse,
  posthogInstance?: PostHog
) => {
  if (graphQLErrors && graphQLErrors?.length > 0) {
    for (const [index, error] of graphQLErrors.entries()) {
      // only process first error to avoid multiple toasts
      if (index === 0 && !networkError) {
        // if error for refresh request, refresh token is not valid anymore
        if (operation.operationName === 'OAuth2Refresh') {
          // Already on logout page, don't redirect again to prevent loop
          if (window.location.pathname.includes('/logout')) {
            return
          }
          // /impersonate only renders impersonation errors; any session in
          // its cookies belongs to another tab. Leave the error on screen
          // instead of logging that session out.
          if (window.location.pathname.startsWith('/impersonate')) {
            return
          }
          toast({
            icon: 'error',
            title: 'Session expired',
            subtitle: 'Your session has expired. Please log in again.',
            variant: 'error'
          })

          ssrErrorTracking({
            posthogInstance,
            fingerprint: 'sessionExpired',
            error,
            properties: {
              error,
              status: (error.extensions?.status as number) ?? 'na',
              ...getOperationErrorProperties(operation)
            }
          })

          const isImpersonating =
            Cookies.get(AppCookie.Impersonating) === 'true'

          // reason=expired lets /expired show the inactivity copy instead of
          // the manual "securely logged out" one (see LoggedOutLayout)
          window.location.href = isImpersonating
            ? '/logout-impersonation'
            : '/logout?reason=expired'
          return
        }

        const ssrStatus = (error.extensions?.status as number) ?? 'na'
        // Expected client-class (4xx) SSR failures are not exceptions — e.g. an
        // unknown payments-portal subdomain resolving to a 404 OAuth2Url, which
        // is subdomain-scanner noise hitting the wildcard *.alternativepayments.io
        // portal and was the #1 error by volume. Don't report them to PostHog;
        // only genuine 5xx (and unknown/no-status) failures get captured. The
        // toast still fires below so user-facing behavior is unchanged.
        const isClientError =
          typeof ssrStatus === 'number' && ssrStatus >= 400 && ssrStatus < 500

        if (!isClientError) {
          ssrErrorTracking({
            posthogInstance,
            fingerprint: 'ssrError',
            error,
            properties: {
              error,
              status: ssrStatus,
              graphUri: process.env.NEXT_PUBLIC_GRAPH_URI,
              // eslint-disable-next-line alternative/no-invalid-env-usage
              deployEnv: process.env.NEXT_PUBLIC_VERCEL_ENV,
              errorCode: (error.extensions?.code as string) ?? undefined,
              errorPath: Array.isArray(error.path)
                ? error.path.join('.')
                : undefined,
              errorMessage: error.message,
              ...getOperationErrorProperties(operation)
            }
          })
        }
        toast(
          {
            icon: 'error',
            title: 'Error',
            subtitle: error.message || 'An unknown error occurred',
            variant: 'error'
          },
          { id: 'ssr-error' }
        )
      }
    }
  }

  if (networkError) {
    ssrErrorTracking({
      posthogInstance,
      fingerprint: 'networkError',
      error: networkError,
      properties: {
        ...getTransportErrorProperties(networkError),
        ...getOperationErrorProperties(operation)
      }
    })
  }
}

// Exported so non-Apollo callsites that must read the JWT directly (e.g. the
// partner assistant's multipart upload + binary file download, which can't go
// through the HTTP link) can reuse the SAME distributed-lock refresh as the
// error link. Honors the AUTHENTICATION.md rule: no custom token refresh — the
// shared RefreshLock cookie serializes concurrent tabs/requests.
export const refreshLock = async (refreshFunction: () => Promise<void>) => {
  const isRefreshLocked = Cookies.get(AppCookie.RefreshLock)

  try {
    if (!isRefreshLocked) {
      Cookies.set(
        AppCookie.RefreshLock,
        'true',
        cookieOptions(AppCookie.RefreshLock, { httpOnly: false })
      )
      await refreshFunction()
      Cookies.remove(AppCookie.RefreshLock)
    } else {
      await waitForRefreshCompletion()
    }
  } catch (err) {
    Cookies.remove(AppCookie.RefreshLock)
    throw new GraphQLError(err)
  }
}

const waitForRefreshCompletion = () =>
  new Promise<void>(resolve => {
    const interval = setInterval(() => {
      const isRefreshLocked = Cookies.get(AppCookie.RefreshLock)

      if (!isRefreshLocked) {
        clearInterval(interval)
        resolve()
      }
    }, 100)
  })

export const retryLink = new RetryLink({
  attempts: (count, operation, error) => {
    if (error && count < 5) {
      return true
    }
    if (error) {
      // Capture is owned by the error link (handleErrors / handleSSRErrors)
      // — capturing here too produced the doubled "Failed to fetch" issue.
      // Callers that suppress network exception tracking also own their own
      // sticky UI; do not toast-storm on poll retries.
      if (operation.getContext().suppressNetworkErrorTracking === true) {
        return false
      }
      toast({
        icon: 'error',
        title: 'Error',
        subtitle: error?.message || 'An unknown error occurred',
        variant: 'error'
      })
    }
    return false
  }
})

export const httpLink = createHttpLink({
  uri: process.env.NEXT_PUBLIC_GRAPH_URI
})

export const ssrErrorLink = onError(errors => handleSSRErrors(errors))

export const requestIDLink = new ApolloLink((operation, forward) => {
  operation.setContext({
    headers: {
      ...operation.getContext().headers,
      [requestIdHeader]: uuidv4()
    }
  })
  return forward(operation)
})
